import { useMemo } from 'react'
import { $api } from '../api/queryClient'

/**
 * The uids of an asset's whole subtree, the asset itself included.
 *
 * Every parent picker needs it: the backend refuses a move onto the asset
 * itself or onto one of its descendants (400, spec 2026-09-18-01), and a
 * picker that still offers them turns a rule into an error message. Filtering
 * self alone was what the identity row did — and a descendant is exactly the
 * mistake a user makes when restructuring.
 *
 * Read through `ancestor_uid`, which matches on the stored ancestry
 * (`parent_asset_ids @> ARRAY[id]`) and therefore already includes the asset
 * itself. Capped: a subtree deeper than the cap simply leaves its tail
 * offered, and the backend still refuses it.
 */
export function useAssetDescendantUids(org: string, assetUid: string): Set<string> {
    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets',
        {
            params: {
                path: { orgCode: org },
                query: { ancestor_uid: assetUid, limit: 500 },
            },
        },
        { enabled: Boolean(org && assetUid) },
    )

    return useMemo(() => {
        const rows = ((data as { data?: Array<Record<string, unknown>> } | undefined)?.data) || []
        const out = new Set<string>(rows.map((a) => String(a.uid)))
        // The asset itself, whatever the ancestry column says: it is NOT
        // reliably self-inclusive (an aggregating edge rewrites it without the
        // row's own id, spec 2026-09-11-01).
        if (assetUid) out.add(assetUid)

        return out
    }, [data, assetUid])
}
